import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { THEME, shadows } from '../constants/theme';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon,
  CreditCardIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import { useAnalytics } from '../hooks/useAnalytics';

const PaymentModal = ({ invoice, onClose, onSave }) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    amount: invoice.total_amount - (invoice.paid_amount || 0),
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Bank Transfer',
    notes: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Add payment record
      const { error: paymentError } = await supabase
        .from('payments')
        .insert([{
          invoice_id: invoice.invoice_id,
          user_id: user.id,
          ...formData,
        }]);

      if (paymentError) throw paymentError;

      // Update invoice status if fully paid
      const newPaidAmount = (invoice.paid_amount || 0) + parseFloat(formData.amount);
      const newStatus = newPaidAmount >= invoice.total_amount ? 'Paid' : 'Sent';

      const { error: invoiceError } = await supabase
        .from('invoices')
        .update({ 
          status: newStatus,
          paid_amount: newPaidAmount,
        })
        .eq('invoice_id', invoice.invoice_id);

      if (invoiceError) throw invoiceError;

      onSave();
    } catch (error) {
      console.error('Error recording payment:', error);
      setError('Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6" style={{ boxShadow: shadows.xl }}>
        <h2 className="text-xl font-bold mb-4" style={{ color: THEME.text }}>
          Record Payment
        </h2>

        {error && (
          <div 
            className="rounded-lg p-4 mb-4 text-sm"
            style={{ backgroundColor: `${THEME.error}15`, color: THEME.error }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Amount
            </label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
              min="0.01"
              max={invoice.total_amount - (invoice.paid_amount || 0)}
              step="0.01"
            />
            <p className="mt-1 text-sm" style={{ color: THEME.subtext }}>
              Remaining: ${(invoice.total_amount - (invoice.paid_amount || 0)).toFixed(2)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Payment Date
            </label>
            <input
              type="date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Payment Method
            </label>
            <select
              name="payment_method"
              value={formData.payment_method}
              onChange={handleInputChange}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              required
            >
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Cash">Cash</option>
              <option value="Credit Card">Credit Card</option>
              <option value="Check">Check</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: THEME.text }}>
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={2}
              className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              placeholder="Add any payment notes..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg"
              style={{ color: THEME.text }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
              style={{ 
                backgroundColor: THEME.primary,
                opacity: saving ? 0.7 : 1
              }}
            >
              {saving ? 'Saving...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function InvoiceDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [invoice, setInvoice] = useState({
    invoice_number: '',
    client_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'Draft',
    subtotal: 0,
    tax_rate: 20,
    tax_amount: 0,
    total_amount: 0,
    paid_amount: 0,
    notes: '',
    terms: '',
    items: [],
  });
  const [publicInvoiceData, setPublicInvoiceData] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const { logEvent } = useAnalytics();

  useEffect(() => {
    if (user) {
      fetchClients();
      if (!isNew) {
        fetchInvoice();
      } else {
        generateInvoiceNumber();
      }
    }
  }, [user, id]);

  useEffect(() => {
    if (user && !isNew) {
      fetchPublicInvoice();
    }
  }, [user, id]);

  useEffect(() => {
    if (id) {
      logEvent('InvoiceDetail', 'View Invoice', `Invoice ID: ${id}`);
    }
  }, [id]);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', user.id)
        .order('client_name');

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Failed to load clients');
    }
  };

  const fetchInvoice = async () => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          clients (
            client_id,
            client_name,
            client_email
          ),
          items:invoice_items (*),
          payments (*)
        `)
        .eq('invoice_id', id)
        .single();

      if (error) throw error;
      setInvoice({
        ...data,
        invoice_date: data.invoice_date?.split('T')[0],
        due_date: data.due_date?.split('T')[0],
        items: data.items || [],
      });
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const generateInvoiceNumber = async () => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('invoice_number')
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      let nextNumber = 1;
      if (data && data.length > 0 && data[0].invoice_number) {
        const lastNumber = parseInt(data[0].invoice_number.split('-')[1]);
        nextNumber = lastNumber + 1;
      }

      setInvoice(prev => ({
        ...prev,
        invoice_number: `INV-${String(nextNumber).padStart(4, '0')}`
      }));
    } catch (error) {
      console.error('Error generating invoice number:', error);
      setError('Failed to generate invoice number');
    }
  };

  const calculateTotals = (items = []) => {
    const itemsToCalculate = items || invoice.items || [];
    const subtotal = itemsToCalculate.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + (quantity * price);
    }, 0);
    const tax_rate = parseFloat(invoice.tax_rate) || 0;
    const tax_amount = (subtotal * tax_rate) / 100;
    const total_amount = subtotal + tax_amount;
    return { subtotal, tax_amount, total_amount };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInvoice(prev => {
      const newInvoice = {
        ...prev,
        [name]: value
      };

      // Recalculate totals if tax_rate changes
      if (name === 'tax_rate') {
        const totals = calculateTotals(prev.items);
        return {
          ...newInvoice,
          ...totals
        };
      }

      return newInvoice;
    });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...invoice.items];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    
    const totals = calculateTotals(newItems);
    setInvoice(prev => ({
      ...prev,
      items: newItems,
      ...totals
    }));
  };

  const handleAddItem = () => {
    const newItems = [
      ...invoice.items,
      { description: '', quantity: 1, unit_price: 0 }
    ];
    
    const totals = calculateTotals(newItems);
    setInvoice(prev => ({
      ...prev,
      items: newItems,
      ...totals
    }));
  };

  const handleRemoveItem = (index) => {
    const newItems = invoice.items.filter((_, i) => i !== index);
    const totals = calculateTotals(newItems);
    setInvoice(prev => ({
      ...prev,
      items: newItems,
      ...totals
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      logEvent('InvoiceDetail', 'Save Invoice', `Invoice ID: ${id}`);
      const totals = calculateTotals(invoice.items);
      const invoiceData = {
        ...invoice,
        user_id: user.id,
        tax_rate: parseFloat(invoice.tax_rate) || 0,
        tax_amount: totals.tax_amount,
        total_amount: totals.total_amount,
        subtotal: totals.subtotal,
        notes: invoice.notes || '',
        terms: invoice.terms || '',
        // Remove nested objects that shouldn't be sent
        items: undefined,
        clients: undefined,
        payments: undefined
      };

      if (isNew) {
        // Create invoice
        const formattedInvoice = {
          ...invoice,
          items: undefined,
          clients: undefined,
          payments: undefined
        };
        const {clients, items, payments, paid_amount, ...invoiceData} = formattedInvoice;
        console.log('invoiceData', invoiceData);
        const { data: newInvoice, error: invoiceError } = await supabase
          .from('invoices')
          .insert([{...invoiceData, user_id: user.id}])
          .select()
          .single();

        if (invoiceError) throw invoiceError;

        // Create invoice items
        if (invoice.items && invoice.items.length > 0) {
          const items = invoice.items.map(item => ({
            invoice_id: newInvoice.invoice_id,
            user_id: user.id,
            description: item.description || '',
            quantity: parseFloat(item.quantity) || 0,
            unit_price: parseFloat(item.unit_price) || 0
          }));

          const { error: itemsError } = await supabase
            .from('invoice_items')
            .insert(items);

          if (itemsError) throw itemsError;
        }

        // Show success message
        setError({ type: 'success', message: 'Invoice created successfully!' });

        setSaving(false);
        
        // Navigate after a short delay to show the success message
        setTimeout(() => {
          navigate(`/invoices/${newInvoice.invoice_id}`);
        }, 1500);
      } else {
        // Update invoice
        const { error: invoiceError } = await supabase
          .from('invoices')
          .update(invoiceData)
          .eq('invoice_id', id);

        if (invoiceError) throw invoiceError;

        // Delete existing items
        const { error: deleteError } = await supabase
          .from('invoice_items')
          .delete()
          .eq('invoice_id', id);

        if (deleteError) throw deleteError;

        // Create new items
        const items = invoice.items.map(item => ({
          invoice_id: id,
          user_id: user.id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price
        }));

        const { error: itemsError } = await supabase
          .from('invoice_items')
          .insert(items);

        if (itemsError) throw itemsError;

        // Show success message
        setError({ type: 'success', message: 'Invoice updated successfully!' });
        
        // Fetch updated data after a short delay
        setTimeout(async () => {
          await fetchInvoice();
          setSaving(false);
        }, 1500);
      }
      logEvent('InvoiceDetail', 'Save Invoice Success', `Invoice ID: ${id}`);
    } catch (error) {
      console.error('Error saving invoice:', error);
      setError({ type: 'error', message: 'Failed to save invoice' });
      setSaving(false);
      logEvent('InvoiceDetail', 'Save Invoice Error', error.message);
    }
  };

  const handlePayment = () => {
    setShowPaymentModal(true);
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
  };

  const handleSavePayment = async () => {
    await fetchInvoice();
    handleClosePaymentModal();
  };

  const fetchPublicInvoice = async () => {
    try {
      const { data, error } = await supabase
        .from('public_invoices')
        .select('*')
        .eq('invoice_id', id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setPublicInvoiceData(data);
    } catch (error) {
      console.error('Error fetching public invoice:', error);
    }
  };

  const handleShare = async () => {
    try {
      setShareLoading(true);
      let shareableInvoice;

      // Check if a public invoice already exists
      const { data: existingInvoice, error: fetchError } = await supabase
        .from('public_invoices')
        .select('*')
        .eq('invoice_id', id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 is "not found" error
        throw fetchError;
      }

      if (existingInvoice) {
        shareableInvoice = existingInvoice;
      } else {
        // Create new public invoice
        const { data: newInvoice, error: createError } = await supabase
          .from('public_invoices')
          .insert({
            invoice_id: id,
            user_id: user.id,
            customization_options: {
              showPrices: true,
              showLineItems: true,
            },
          })
          .select()
          .single();

        if (createError) throw createError;
        shareableInvoice = newInvoice;
      }

      // Get business name
      const { data: businessData, error: businessError } = await supabase
        .from('business_profile')
        .select('business_name')
        .eq('user_id', user.id)
        .single();

      if (businessError) throw businessError;

      // Generate shareable link
      const shareableLink = `${window.location.origin}/invoice/${shareableInvoice.id}`;

      // Update invoice status to "Sent"
      const { error: updateError } = await supabase
        .from('invoices')
        .update({ status: 'Sent' })
        .eq('invoice_id', id);

      if (updateError) throw updateError;

      // Copy link to clipboard
      await navigator.clipboard.writeText(shareableLink);
      alert(`Invoice link copied to clipboard! Share it with ${businessData?.business_name ? businessData.business_name + "'s" : 'your'} client.`);

      // Update local state
      setInvoice(prev => ({ ...prev, status: 'Sent' }));
      fetchPublicInvoice();
    } catch (err) {
      console.error('Error sharing invoice:', err);
      setError({ type: 'error', message: 'Could not share the invoice. Please try again.' });
    } finally {
      setShareLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      logEvent('InvoiceDetail', 'Status Change', `Invoice ${id} to ${status}`);
      const { error: invoiceError } = await supabase
        .from('invoices')
        .update({ status })
        .eq('invoice_id', id);

      if (invoiceError) throw invoiceError;

      setInvoice(prev => ({ ...prev, status }));
      logEvent('InvoiceDetail', 'Status Change Success', `Invoice ${id} to ${status}`);
    } catch (error) {
      console.error('Error changing status:', error);
      setError({ type: 'error', message: 'Failed to change status' });
      logEvent('InvoiceDetail', 'Status Change Error', error.message);
    }
  };

  const handleSendEmail = async () => {
    try {
      logEvent('InvoiceDetail', 'Send Email', `Invoice ID: ${id}`);
      // ... existing email logic ...
      logEvent('InvoiceDetail', 'Send Email Success', `Invoice ID: ${id}`);
    } catch (error) {
      logEvent('InvoiceDetail', 'Send Email Error', error.message);
    }
  };

  const handleGeneratePDF = async () => {
    try {
      logEvent('InvoiceDetail', 'Generate PDF', `Invoice ID: ${id}`);
      // ... existing PDF generation logic ...
      logEvent('InvoiceDetail', 'Generate PDF Success', `Invoice ID: ${id}`);
    } catch (error) {
      logEvent('InvoiceDetail', 'Generate PDF Error', error.message);
    }
  };

  const handleRecordPayment = async (amount, method) => {
    try {
      logEvent('InvoiceDetail', 'Record Payment', `Invoice ID: ${id}, Amount: ${amount}, Method: ${method}`);
      // ... existing payment recording logic ...
      logEvent('InvoiceDetail', 'Record Payment Success', `Invoice ID: ${id}`);
    } catch (error) {
      logEvent('InvoiceDetail', 'Record Payment Error', error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: `${THEME.primary} transparent` }} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/invoices')}
            className="mr-4 text-gray-400 hover:text-gray-500"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: THEME.text }}>
              {isNew ? 'New Invoice' : `Invoice #${invoice.invoice_number}`}
            </h1>
            <p style={{ color: THEME.subtext }}>
              {isNew ? 'Create a new invoice' : 'Edit invoice details'}
            </p>
          </div>
        </div>
        {!isNew && (
          <div className="flex items-center space-x-4">
            {publicInvoiceData && (
              <a
                href={`/invoice/${publicInvoiceData.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-lg transition-all duration-200"
                style={{ 
                  backgroundColor: `${THEME.primary}15`,
                  color: THEME.primary
                }}
              >
                View Public Invoice
              </a>
            )}
            {publicInvoiceData && (
              <a
                href={`/invoices/${publicInvoiceData.invoice_id}/interactions`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 rounded-lg transition-all duration-200"
                style={{ 
                  backgroundColor: `${THEME.primary}15`,
                  color: THEME.primary
                }}
              >
                Analytics
              </a>
            )}
            <button
              onClick={handleShare}
              disabled={shareLoading}
              className="inline-flex items-center px-4 py-2 rounded-lg text-white transition-all duration-200"
              style={{ 
                background: THEME.primary,
                boxShadow: shadows.md,
                opacity: shareLoading ? 0.7 : 1
              }}
            >
              {shareLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                <>
                  <ShareIcon className="w-5 h-5 mr-2" />
                  Share Invoice
                </>
              )}
            </button>
            {invoice.status !== 'Paid' && (
              <button
                onClick={handlePayment}
                className="inline-flex items-center px-4 py-2 rounded-lg text-white transition-all duration-200"
                style={{ 
                  background: THEME.primary,
                  boxShadow: shadows.md,
                }}
              >
                <CreditCardIcon className="w-5 h-5 mr-2" />
                Record Payment
              </button>
            )}
          </div>
        )}
      </div>
      
      {publicInvoiceData && (
        <div className="bg-white rounded-xl p-6 mb-6" style={{ boxShadow: shadows.md }}>
          <h2 className="text-lg font-semibold mb-4" style={{ color: THEME.text }}>
            Public Invoice Settings
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Public Link
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={`${window.location.origin}/invoice/${publicInvoiceData.id}`}
                  readOnly
                  className="flex-1 rounded-lg border-gray-300 bg-gray-50"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/invoice/${publicInvoiceData.id}`);
                    alert('Link copied to clipboard!');
                  }}
                  className="px-4 py-2 text-sm font-medium rounded-lg"
                  style={{ 
                    backgroundColor: `${THEME.primary}15`,
                    color: THEME.primary
                  }}
                >
                  Copy Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div 
          className="rounded-lg p-4 mb-6 text-sm"
          style={{ 
            backgroundColor: error.type === 'success' ? `${THEME.success}15` : `${THEME.error}15`,
            color: error.type === 'success' ? THEME.success : THEME.error
          }}
        >
          {error.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Client
              </label>
              <select
                name="client_id"
                value={invoice.client_id}
                onChange={handleInputChange}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                required
              >
                <option value="">Select a client</option>
                {clients.map(client => (
                  <option key={client.client_id} value={client.client_id}>
                    {client.client_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Status
              </label>
              <select
                name="status"
                value={invoice.status}
                onChange={handleStatusChange}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
              >
                <option value="Draft">Draft</option>
                <option value="Sent">Sent</option>
                <option value="Paid">Paid</option>
                <option value="Overdue">Overdue</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Invoice Date
              </label>
              <input
                type="date"
                name="invoice_date"
                value={invoice.invoice_date}
                onChange={handleInputChange}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Due Date
              </label>
              <input
                type="date"
                name="due_date"
                value={invoice.due_date}
                onChange={handleInputChange}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                required
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold" style={{ color: THEME.text }}>Items</h2>
            <button
              type="button"
              onClick={handleAddItem}
              className="inline-flex items-center px-3 py-1.5 text-sm rounded-lg transition-all duration-200"
              style={{ 
                backgroundColor: `${THEME.primary}15`,
                color: THEME.primary
              }}
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Item
            </button>
          </div>

          <div className="space-y-4">
            {invoice.items.map((item, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                    placeholder="Item description"
                    className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                    required
                  />
                </div>
                <div className="w-24">
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))}
                    placeholder="Qty"
                    className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                    required
                    min="1"
                  />
                </div>
                <div className="w-32">
                  <input
                    type="number"
                    value={item.unit_price}
                    onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value))}
                    placeholder="Price"
                    className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="w-32 pt-2 text-right" style={{ color: THEME.text }}>
                  ${(item.quantity * item.unit_price).toFixed(2)}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="pt-2 text-gray-400 hover:text-red-500"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            ))}

            {invoice.items.length === 0 && (
              <p className="text-center py-4" style={{ color: THEME.subtext }}>
                No items added yet. Click "Add Item" to get started.
              </p>
            )}

            <div className="border-t border-gray-200 pt-4 mt-4">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span style={{ color: THEME.subtext }}>Subtotal</span>
                    <span style={{ color: THEME.text }}>${invoice.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span style={{ color: THEME.subtext }}>Tax Rate</span>
                    <input
                      type="number"
                      name="tax_rate"
                      value={invoice.tax_rate}
                      onChange={handleInputChange}
                      className="w-20 text-right rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                    <span style={{ color: THEME.subtext }}>%</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: THEME.subtext }}>Tax</span>
                    <span style={{ color: THEME.text }}>${invoice.tax_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold">
                    <span style={{ color: THEME.text }}>Total</span>
                    <span style={{ color: THEME.primary }}>${invoice.total_amount.toFixed(2)}</span>
                  </div>
                  {invoice.paid_amount > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span style={{ color: THEME.success }}>Paid</span>
                        <span style={{ color: THEME.success }}>${invoice.paid_amount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span style={{ color: THEME.text }}>Balance</span>
                        <span style={{ color: THEME.text }}>
                          ${(invoice.total_amount - invoice.paid_amount).toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6" style={{ boxShadow: shadows.md }}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Notes
              </label>
              <textarea
                name="notes"
                value={invoice.notes}
                onChange={handleInputChange}
                rows={3}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                placeholder="Add any notes for the client..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: THEME.text }}>
                Terms & Conditions
              </label>
              <textarea
                name="terms"
                value={invoice.terms}
                onChange={handleInputChange}
                rows={3}
                className="w-full rounded-lg border-gray-300 focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                placeholder="Add your terms and conditions..."
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-200"
            style={{ 
              backgroundColor: THEME.primary,
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Saving...' : (isNew ? 'Create Invoice' : 'Save Changes')}
          </button>
        </div>
      </form>

      {showPaymentModal && (
        <PaymentModal
          invoice={invoice}
          onClose={handleClosePaymentModal}
          onSave={handleSavePayment}
        />
      )}
    </div>
  );
} 